// Shared file-import pipeline for the design editor: used by the Media
// panel's upload tab AND drag-drop directly onto the canvas. Handles images
// (as data URLs) and PDFs (first page rasterized at 2× via pdf.js).
import * as pdfjsLib from 'pdfjs-dist';
// studioCloud runs pdfjs-dist 5.x (the desktop is on 3.x), where the worker
// ships as an ES module — hence .mjs. Same local-bundle contract: no CDN.
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import i18n from '../../../../i18n';

// Local bundled worker (no CDN dependency) — safe to set repeatedly.
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

function importImageFile(file, editor, at) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      editor?.addImage(event.target?.result, at ? { at } : undefined);
      resolve(true);
    };
    reader.onerror = () => resolve(false);
    reader.readAsDataURL(file);
  });
}

async function importPdfFile(file, editor, at, toast) {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

    // First page only (multi-page choice is future work)
    const page = await pdf.getPage(1);
    const scale = 2;
    const viewport = page.getViewport({ scale });

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.width = viewport.width;
    canvas.height = viewport.height;

    await page.render({ canvasContext: context, viewport }).promise;

    editor?.addImage(canvas.toDataURL('image/png'), at ? { at } : undefined);
    pdf.destroy();
    return true;
  } catch (error) {
    console.error('Error processing PDF:', error);
    toast?.showError(i18n.t('editor:media.pdfImportFailed'));
    return false;
  }
}

/**
 * Import a FileList/array of files into the editor.
 * `at` (scene coords {x, y}) positions the FIRST file at the drop point;
 * subsequent files cascade from the canvas center as usual.
 */
export async function importFilesToEditor(files, editor, { toast, at } = {}) {
  const list = Array.from(files || []);
  let first = true;
  for (const file of list) {
    const position = first ? at : undefined;
    if (file.type === 'application/pdf') {
      await importPdfFile(file, editor, position, toast);
    } else if (file.type.startsWith('image/')) {
      await importImageFile(file, editor, position);
    }
    first = false;
  }
}
